/**
 * Pure State Mutations - Shared between Commands and Events
 * 
 * These functions are pure transformations that define HOW state changes.
 * They are used by:
 * - Event handlers: Apply to IndexedDB (server truth)
 * - Command handlers: Apply to memory state (optimistic updates)
 * 
 * Benefits:
 * - Single source of truth for state transformation logic
 * - Easier to test (pure functions)
 * - No duplication between commands and events
 * 
 * NOTE: Using domain models directly - no separate mutation types needed
 */

import type { Scenario, List, Item, ScenarioItem, Flow, FlowRanking } from '../domain/models'
import { getCurrentISOTimestamp } from '../utils/timestamp'

// Re-export domain models for backward compatibility
export type { Scenario, List, Item, ScenarioItem, Flow }

/**
 * List Item Mutations (Dictionary-based)
 */

export function addItemToList(list: List, item: Item): List {
  const currentItems = list.items || {}
  // Avoid duplicates
  if (currentItems[item.id]) {
    return list
  }
  return {
    ...list,
    items: { ...currentItems, [item.id]: item },
    updatedAt: getCurrentISOTimestamp()
  }
}

export function addItemsToList(list: List, newItems: Item[]): List {
  const currentItems = list.items || {}
  const updatedItems = { ...currentItems }
  
  let hasChanges = false
  for (const item of newItems) {
    if (!updatedItems[item.id]) {
      updatedItems[item.id] = item
      hasChanges = true
    }
  }
  
  if (!hasChanges) return list
  
  return {
    ...list,
    items: updatedItems,
    updatedAt: getCurrentISOTimestamp()
  }
}

export function updateItemInList(list: List, itemId: string, updateFn: (item: Item) => Item): List {
  const currentItems = list.items || {}
  const item = currentItems[itemId]
  
  if (!item) return list
  
  return {
    ...list,
    items: { ...currentItems, [itemId]: updateFn(item) },
    updatedAt: getCurrentISOTimestamp()
  }
}

export function deleteItemFromList(list: List, itemId: string): List {
  const currentItems = list.items || {}
  if (!currentItems[itemId]) return list
  
  const { [itemId]: _deleted, ...remaining } = currentItems
  return {
    ...list,
    items: remaining,
    updatedAt: getCurrentISOTimestamp()
  }
}

/**
 * Item Mutations
 */

export function applyItemCompletion(item: Item, isComplete: boolean): Item {
  return {
    ...item,
    isCompleted: isComplete,
    updatedAt: getCurrentISOTimestamp()
  }
}

export function applyItemRename(item: Item, newName: string): Item {
  return {
    ...item,
    name: newName,
    updatedAt: getCurrentISOTimestamp()
  }
}

export function applyItemReorder(item: Item, newOrder: number): Item {
  return {
    ...item,
    order: newOrder,
    updatedAt: getCurrentISOTimestamp()
  }
}

export function applyItemQuantity(item: Item, quantity: number): Item {
  return {
    ...item,
    quantity,
    updatedAt: getCurrentISOTimestamp()
  }
}

export function createItem(params: {
  itemId: string
  listId: string
  scenarioId: string
  itemName: string
  quantity?: number
  sortOrder?: number
}): Item {
  const now = getCurrentISOTimestamp()
  return {
    id: params.itemId,
    listId: params.listId,
    scenarioId: params.scenarioId,
    name: params.itemName,
    quantity: params.quantity ?? 1,
    isCompleted: false,
    order: params.sortOrder ?? 0,
    createdAt: now,
    updatedAt: now
  }
}

export function createItems(params: {
  items: Array<{
    itemId: string
    itemName: string
    order: number
    quantity?: number
  }>
  listId: string
  scenarioId: string
}): Item[] {
  const now = getCurrentISOTimestamp()
  return params.items.map(item => ({
    id: item.itemId,
    listId: params.listId,
    scenarioId: params.scenarioId,
    name: item.itemName,
    quantity: item.quantity ?? 1,
    isCompleted: false,
    order: item.order,
    createdAt: now,
    updatedAt: now
  }))
}

/**
 * List Mutations
 */

export function createList(params: {
  listId: string
  scenarioId: string
  listName: string
  description?: string
  sortOrder?: number
}): List {
  const now = getCurrentISOTimestamp()
  return {
    id: params.listId,
    scenarioId: params.scenarioId,
    name: params.listName,
    description: params.description || '',
    isDeleted: false,
    order: params.sortOrder,
    createdAt: now,
    updatedAt: now
  }
}

export function applyListRename(list: List, newName: string, newDescription?: string): List {
  return {
    ...list,
    name: newName,
    description: newDescription,
    updatedAt: getCurrentISOTimestamp()
  }
}

/** * Flow Mutations
 */

export function createFlow(params: {
  flowId: string
  scenarioId: string
  flowName: string
  description?: string
  color?: string
}): Flow {
  const now = getCurrentISOTimestamp()
  return {
    id: params.flowId,
    scenarioId: params.scenarioId,
    name: params.flowName,
    description: params.description,
    color: params.color,
    rankings: {},
    createdAt: now,
    updatedAt: now
  }
}

export function applyFlowRename(
  flow: Flow, 
  newName: string, 
  newDescription?: string,
  newColor?: string
): Flow {
  const updates: Partial<Flow> = {
    name: newName,
    updatedAt: getCurrentISOTimestamp()
  }
  
  if (newDescription !== undefined) {
    updates.description = newDescription
  }

  if (newColor !== undefined) {
    updates.color = newColor
  }
  
  return {
    ...flow,
    ...updates
  }
}

export function applyFlowRankingsUpdate(
  flow: Flow,
  rankings: Record<string, FlowRanking>
): Flow {
  return {
    ...flow,
    rankings: {
      ...flow.rankings,
      ...rankings
    },
    updatedAt: getCurrentISOTimestamp()
  }
}

/**
 * Scenario Mutations
 */

export function createScenario(params: {
  scenarioId: string
  scenarioName: string
  description?: string
  role?: string
}): Scenario {
  const now = getCurrentISOTimestamp()
  const roleCandidate = String(params.role || 'owner').toLowerCase()
  const role: 'owner' | 'editor' = roleCandidate === 'owner' ? 'owner' : 'editor'
  return {
    id: params.scenarioId,
    name: params.scenarioName,
    description: params.description || '',
    role,
    sequenceNumber: 0, // Start at 0, ready for events
    isDeleted: false,
    createdAt: now,
    updatedAt: now
  }
}

export function applyScenarioRename(scenario: Scenario, newName: string, newDescription?: string): Scenario {
  const updates: Partial<Scenario> = {
    name: newName,
    updatedAt: getCurrentISOTimestamp()
  }
  
  if (newDescription !== undefined) {
    updates.description = newDescription || ''
  }
  
  return {
    ...scenario,
    ...updates
  }
}

export function applyScenarioDeletion(scenario: Scenario): Scenario {
  return {
    ...scenario,
    isDeleted: true,
    updatedAt: getCurrentISOTimestamp()
  }
}

/**
 * Scenario Item Mutations
 */
export function applyScenarioItemUsage(
  items: ScenarioItem[],
  args: {
    itemId: string
    itemName: string
    lastUsedAt: string
    quantity?: number
  }
): ScenarioItem[] {
  const itemId = String(args.itemId || '').trim()
  const itemName = String(args.itemName || '').trim()
  const lastUsedAt = String(args.lastUsedAt || '').trim() || getCurrentISOTimestamp()

  if (!itemId) throw new Error('applyScenarioItemUsage: itemId is required')
  if (!itemName) throw new Error('applyScenarioItemUsage: itemName is required')

  const existingIndex = items.findIndex(s => s.id === itemId)

  if (existingIndex >= 0) {
    const existing = items[existingIndex]
    const newItems = [...items]
    newItems[existingIndex] = {
      ...existing,
      quantity: typeof args.quantity === 'number' && args.quantity >= 1 ? args.quantity : (existing.quantity ?? 1),
      usageCount: existing.usageCount + 1,
      lastUsedAt
    }
    return newItems
  }

  return [
    ...items,
    {
      id: itemId,
      name: itemName,
      quantity: typeof args.quantity === 'number' && args.quantity >= 1 ? args.quantity : 1,
      usageCount: 1,
      lastUsedAt
    }
  ]
}

export function applyScenarioItemQuantity(
  items: ScenarioItem[],
  args: {
    itemId: string
    quantity: number
  }
): ScenarioItem[] {
  const itemId = String(args.itemId || '').trim()
  const quantity = typeof args.quantity === 'number' && Number.isFinite(args.quantity)
    ? Math.max(1, args.quantity)
    : 1

  if (!itemId) throw new Error('applyScenarioItemQuantity: itemId is required')

  const existingIndex = items.findIndex(s => s.id === itemId)
  if (existingIndex < 0) return items

  const existing = items[existingIndex]
  const existingQuantity = typeof existing.quantity === 'number' && existing.quantity >= 1 ? existing.quantity : 1
  if (existingQuantity === quantity) return items

  const next = [...items]
  next[existingIndex] = {
    ...existing,
    quantity
  }
  return next
}

export function applyScenarioItemRename(
  items: ScenarioItem[],
  args: {
    itemId: string
    newName: string
  }
): ScenarioItem[] {
  const itemId = String(args.itemId || '').trim()
  const newName = String(args.newName || '').trim()

  if (!itemId) throw new Error('applyScenarioItemRename: itemId is required')
  if (!newName) throw new Error('applyScenarioItemRename: newName is required')

  const existingIndex = items.findIndex(s => s.id === itemId)
  if (existingIndex < 0) return items

  const existing = items[existingIndex]
  if (existing.name === newName) return items

  const next = [...items]
  next[existingIndex] = {
    ...existing,
    name: newName
  }
  return next
}

export function applyDeleteScenarioItems(items: ScenarioItem[], itemIdsToDelete: string[]): ScenarioItem[] {
  const idsToDelete = new Set(itemIdsToDelete)
  return items.filter(s => !idsToDelete.has(s.id))
}


